import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import { SyncProvider } from "./sync.js";

/**
 * What the capability broker needs from object storage. An interface, not the
 * class, so a test can hand the broker an in-memory object without an
 * IndexedDB and without a relay.
 */
export interface ObjectSource {
  acquire(id: string): Promise<ObjectHandle>;
  release(id: string): void;
}

export type ObjectHandle = {
  id: string;
  content: () => string;
  setContent: (next: string) => void;
  observe: (handler: (content: string) => void) => () => void;
};

type Entry = {
  handle: ObjectHandle;
  doc: Y.Doc;
  persistence: IndexeddbPersistence;
  sync: SyncProvider | null;
  refs: number;
};

/**
 * One document per object, loaded lazily (§6.2).
 *
 * OPEN QUESTION §10.1, answered: **one document per object with an explicit
 * lifecycle, not `Y.Doc` subdocuments.** §6.2 prefers subdocuments so that
 * sync, persistence and offline behaviour stay uniform, and that reasoning is
 * sound — but it does not pay off here. `y-indexeddb` binds to a single
 * `Y.Doc`, so a subdocument still needs its own `IndexeddbPersistence`, and our
 * relay addresses one channel per `doc_id`, so it still needs its own provider.
 * The subdocument wrapper adds the `subdocs` event and load/unload bookkeeping
 * on top of a lifecycle we would be writing anyway, and removes none of it.
 * Uniformity is preserved a different way: this store is the only thing that
 * opens an object, so persistence and sync are wired identically every time.
 *
 * Surfacing this rather than working around it silently, as §10.1 asks. If
 * subdocuments become the better answer — say, once `y-indexeddb` handles them
 * directly — only this file changes.
 */
export class ObjectStore implements ObjectSource {
  readonly #workspaceId: string;
  readonly #syncUrl: string | false;
  readonly #open = new Map<string, Entry>();

  constructor(options: { workspaceId: string; sync: string | false }) {
    this.#workspaceId = options.workspaceId;
    this.#syncUrl = options.sync;
  }

  /** Take a reference to an object's content, loading it if this is the first. */
  async acquire(id: string): Promise<ObjectHandle> {
    const existing = this.#open.get(id);
    if (existing !== undefined) {
      existing.refs++;
      return existing.handle;
    }

    const doc = new Y.Doc({ guid: id });
    const persistence = new IndexeddbPersistence(`gelatos:${this.#workspaceId}:${id}`, doc);
    await persistence.whenSynced;

    const text = doc.getText("content");
    const sync =
      this.#syncUrl === false
        ? null
        : new SyncProvider({
            url: this.#syncUrl,
            workspaceId: this.#workspaceId,
            docId: id,
            doc,
          });

    const handle: ObjectHandle = {
      id,
      content: () => text.toString(),
      setContent: (next) => applyText(doc, text, next),
      observe: (handler) => {
        const listener = (): void => handler(text.toString());
        text.observe(listener);
        return () => text.unobserve(listener);
      },
    };

    this.#open.set(id, { handle, doc, persistence, sync, refs: 1 });
    return handle;
  }

  /** Drop a reference. The document unloads when the last holder lets go. */
  release(id: string): void {
    const entry = this.#open.get(id);
    if (entry === undefined) return;
    entry.refs--;
    if (entry.refs > 0) return;
    this.#open.delete(id);
    entry.sync?.destroy();
    void entry.persistence.destroy();
    entry.doc.destroy();
  }

  destroy(): void {
    for (const id of [...this.#open.keys()]) {
      const entry = this.#open.get(id);
      if (entry !== undefined) entry.refs = 1;
      this.release(id);
    }
  }
}

/**
 * Turn "the content is now this string" into the smallest edit that makes it so.
 *
 * The `object` capability's method is `set` with whole content (§5.3), but
 * replacing the whole text would make two people typing in the same note
 * clobber each other on every keystroke. Narrowing the write to the range that
 * actually changed is what makes concurrent editing converge — and it keeps
 * Yjs out of app code entirely, which is the point.
 */
export function applyText(doc: Y.Doc, text: Y.Text, next: string): void {
  const current = text.toString();
  if (current === next) return;

  let prefix = 0;
  const shortest = Math.min(current.length, next.length);
  while (prefix < shortest && current[prefix] === next[prefix]) prefix++;

  let suffix = 0;
  while (
    suffix < shortest - prefix &&
    current[current.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix++;
  }

  const removed = current.length - prefix - suffix;
  const inserted = next.slice(prefix, next.length - suffix);
  doc.transact(() => {
    if (removed > 0) text.delete(prefix, removed);
    if (inserted.length > 0) text.insert(prefix, inserted);
  });
}
